import {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  JobState,
  ResultKey,
  Scenario,
  TranscriptionFile,
  WorkRecord,
  RESULT_META,
  RESULT_ORDER,
  availableResults,
  captureWork,
  dependencies,
  detectFileKind,
  exportText,
  fileMeta,
  quote,
  rateLabel,
  upsertWork,
  validateFile,
  workStatus,
} from "./core";
import { CoinsEstimateIcon } from "../../components/common/CoinsEstimateIcon";
import { DataIcon } from "./icons";
import {
  PRICE_RULES_HTML,
  SAMPLE_FILES,
  STORAGE_KEY,
  buildTemplatePreview,
  resultData,
} from "./mock";
import "./Transcription.scss";

type ViewMode = "compose" | "result";
type GalleryTab = "examples" | "works";

interface PageState {
  file: TranscriptionFile | null;
  selected: ResultKey[];
  prompt: string;
  cache: { original?: boolean; video?: boolean };
  results: WorkRecord["results"];
  active: ResultKey;
  view: ViewMode;
  supplement: boolean;
  force: ResultKey | null;
  job: JobState | null;
  charged: number;
  scenario: Scenario;
  balance: number;
  works: WorkRecord[];
  workId: string | null;
  detailWorkId: string | null;
  gallery: GalleryTab;
  templatePreview: WorkRecord | null;
  createdAt?: number;
  warnings?: string[];
  lastKeys?: ResultKey[];
  lastCharge?: number;
  lastScenario?: Scenario;
}

const INITIAL_STATE: PageState = {
  file: null,
  selected: [],
  prompt: "",
  cache: {},
  results: {},
  active: "summary",
  view: "compose",
  supplement: false,
  force: null,
  job: null,
  charged: 0,
  scenario: "normal",
  balance: 1234,
  works: [],
  workId: null,
  detailWorkId: null,
  gallery: "examples",
  templatePreview: null,
};

function loadState(): PageState {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
    if (saved?.version === 1 && saved.data && (saved.data.file || Array.isArray(saved.data.works))) {
      const next = { ...INITIAL_STATE, ...saved.data } as PageState;
      next.works = Array.isArray(next.works) ? next.works : [];
      if (next.view === ("processing" as ViewMode)) next.view = "compose";
      if (next.supplement && next.workId && !next.job) {
        next.supplement = false;
        next.force = null;
        next.view = "result";
        next.detailWorkId = next.workId;
      }
      return next;
    }
  } catch {
    /* ignore */
  }
  return INITIAL_STATE;
}

function currentWork(state: PageState): WorkRecord {
  return {
    workId: state.workId || "",
    createdAt: state.createdAt || Date.now(),
    file: state.file as TranscriptionFile,
    selected: state.selected,
    prompt: state.prompt,
    cache: state.cache,
    results: state.results,
    active: state.active,
    job: state.job,
    charged: state.charged,
    lastCharge: state.lastCharge,
    lastScenario: state.lastScenario,
    warnings: state.warnings,
    lastKeys: state.lastKeys,
    scenario: state.scenario,
  };
}

function optionTitle(key: ResultKey, fileType?: TranscriptionFile["type"]) {
  return key === "summary" && fileType === "video" ? "视频总结" : RESULT_META[key][0];
}

export const AiTranscription: FC = () => {
  const [state, setState] = useState<PageState>(loadState);
  const [fileError, setFileError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [toast, setToast] = useState("");
  const [moreOpen, setMoreOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoTitle, setInfoTitle] = useState("");
  const [infoHtml, setInfoHtml] = useState("");
  const [usageOpen, setUsageOpen] = useState(false);
  const [supplementOpen, setSupplementOpen] = useState(false);
  const [supplementDraft, setSupplementDraft] = useState<{
    workId: string;
    keys: ResultKey[];
    force: ResultKey | null;
    prompt: string;
  } | null>(null);
  const [supplementError, setSupplementError] = useState("");
  const [docDemo, setDocDemo] = useState<{
    name: string;
    bytes: number;
    ext: string;
    chars: string;
    pages: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const worksSectionRef = useRef<HTMLElement>(null);
  const infoDialogRef = useRef<HTMLDialogElement>(null);
  const usageDialogRef = useRef<HTMLDialogElement>(null);
  const supplementDialogRef = useRef<HTMLDialogElement>(null);
  const docDialogRef = useRef<HTMLDialogElement>(null);
  const uploadSerial = useRef(0);
  const toastTimer = useRef<number>(0);
  const tickTimer = useRef<number>(0);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 3000);
  }, []);

  useEffect(() => {
    const bind = (el: HTMLDialogElement | null, open: boolean) => {
      if (!el) return;
      if (open) {
        if (!el.open) el.showModal();
      } else if (el.open) {
        el.close();
      }
    };
    bind(infoDialogRef.current, infoOpen);
    bind(usageDialogRef.current, usageOpen);
    bind(supplementDialogRef.current, supplementOpen);
    bind(docDialogRef.current, !!docDemo);
  }, [infoOpen, usageOpen, supplementOpen, docDemo]);

  const persist = useCallback((next: PageState, extraToast?: string) => {
    let works = next.works;
    if (next.workId && next.file) {
      works = upsertWork(works, captureWork(currentWork({ ...next, works })));
    }
    const saved = { ...next, works };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, data: saved }));
    } catch {
      extraToast = extraToast || "浏览器存储空间不足，当前作品在刷新后可能无法恢复。";
    }
    setState(saved);
    if (extraToast) showToast(extraToast);
    return saved;
  }, [showToast]);

  const effectiveFile = useMemo(() => {
    if (!state.file) return null;
    return {
      ...state.file,
      noSpeech: state.scenario === "nospeech" && state.file.type !== "doc",
    };
  }, [state.file, state.scenario]);

  const cacheForQuote = useMemo(() => {
    const cache = { ...state.cache };
    if (state.force === "original") cache.original = false;
    if (state.force === "visual") cache.video = false;
    return cache;
  }, [state.cache, state.force]);

  const quoteResult = useMemo(
    () => quote(effectiveFile, state.selected, cacheForQuote),
    [effectiveFile, state.selected, cacheForQuote],
  );

  const detail = useMemo(() => {
    if (state.templatePreview) return state.templatePreview;
    if (state.detailWorkId === state.workId) return currentWork(state);
    return state.works.find((w) => w.workId === state.detailWorkId) || currentWork(state);
  }, [state]);

  const startInvalid =
    !state.file ||
    !state.selected.length ||
    (state.selected.includes("rewrite") && !state.prompt.trim()) ||
    !!state.job;

  const openInfo = (title: string, html: string) => {
    setInfoTitle(title);
    setInfoHtml(html);
    setInfoOpen(true);
  };

  const selectFile = (file: TranscriptionFile) => {
    if (state.job) return;
    persist({
      ...state,
      templatePreview: null,
      workId: null,
      detailWorkId: null,
      warnings: [],
      lastKeys: undefined,
      lastScenario: undefined,
      file,
      selected: ["summary"],
      cache: {},
      results: {},
      prompt: "",
      job: null,
      charged: 0,
      supplement: false,
      force: null,
      view: "compose",
    });
    setFileError("");
  };

  const resetFile = () => {
    if (state.job) {
      showToast("任务处理中，请完成后新建。");
      return;
    }
    uploadSerial.current += 1;
    if (fileInputRef.current) fileInputRef.current.value = "";
    persist({
      ...state,
      templatePreview: null,
      workId: null,
      detailWorkId: null,
      warnings: [],
      lastKeys: undefined,
      lastScenario: undefined,
      file: null,
      selected: [],
      cache: {},
      results: {},
      prompt: "",
      job: null,
      charged: 0,
      supplement: false,
      force: null,
      view: "compose",
    });
    setFileError("");
  };

  const readMediaDuration = (file: File, kind: "video" | "audio") =>
    new Promise<number>((resolve, reject) => {
      const media = document.createElement(kind);
      const url = URL.createObjectURL(file);
      const timeout = window.setTimeout(() => finish(null), 15000);
      const finish = (value: number | null) => {
        window.clearTimeout(timeout);
        media.onloadedmetadata = null;
        media.onerror = null;
        URL.revokeObjectURL(url);
        media.removeAttribute("src");
        if (value) resolve(value);
        else reject(new Error("当前浏览器无法读取此文件，请换用兼容编码的文件或使用示例体验。"));
      };
      media.preload = "metadata";
      media.onloadedmetadata = () => finish(media.duration);
      media.onerror = () => finish(null);
      media.src = url;
    });

  const handleFiles = async (files: FileList | File[]) => {
    if (state.job || state.supplement) return;
    const list = Array.from(files);
    if (list.length !== 1) {
      setFileError("一次只能上传一个文件。");
      return;
    }
    const file = list[0];
    if (!file) return;
    const err = validateFile(file.name, file.size);
    if (err) {
      setFileError(err);
      return;
    }
    const serial = ++uploadSerial.current;
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    const type = detectFileKind(file.name);
    if (!type) return;
    setFileError("");
    try {
      if (type !== "doc") {
        const seconds = await readMediaDuration(file, type);
        if (serial !== uploadSerial.current) return;
        const limit = validateFile(file.name, file.size, seconds);
        if (limit) throw new Error(limit);
        selectFile({ name: file.name, bytes: file.size, seconds, type, sample: false });
      } else if (ext === "txt") {
        const text = new TextDecoder("utf-8", { fatal: true })
          .decode(await file.arrayBuffer())
          .replace(/^\uFEFF/, "");
        if (serial !== uploadSerial.current) return;
        const chars = [...text].length;
        const limit = validateFile(file.name, file.size, undefined, chars);
        if (limit) throw new Error(limit);
        if (!text.trim()) throw new Error("文档没有可提取的正文。");
        selectFile({
          name: file.name,
          bytes: file.size,
          seconds: 0,
          type,
          chars,
          text,
          sample: false,
        });
      } else {
        if (serial !== uploadSerial.current) return;
        setDocDemo({
          name: file.name,
          bytes: file.size,
          ext,
          chars: "10000",
          pages: "8",
        });
      }
    } catch (error: any) {
      if (serial === uploadSerial.current) {
        setFileError(error?.message || "文件读取失败，请重新选择。");
      }
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const confirmDocDemo = () => {
    if (!docDemo) return;
    const chars = Number(docDemo.chars);
    const pages = Number(docDemo.pages || 0);
    if (
      !Number.isInteger(chars) ||
      chars <= 0 ||
      (docDemo.ext === "pdf" && (!Number.isInteger(pages) || pages <= 0))
    ) {
      showToast("请输入有效的正整数。");
      return;
    }
    const limit = validateFile(docDemo.name, docDemo.bytes, undefined, chars, pages);
    if (limit) {
      showToast(limit);
      return;
    }
    setDocDemo(null);
    selectFile({
      name: docDemo.name,
      bytes: docDemo.bytes,
      type: "doc",
      seconds: 0,
      chars,
      pages,
      manual: true,
      sample: false,
    });
  };

  const prepareJob = (source: PageState): JobState => {
    const f = {
      ...source.file!,
      noSpeech: source.scenario === "nospeech" && source.file!.type !== "doc",
    };
    const cache = { ...source.cache };
    if (source.force === "original") cache.original = false;
    if (source.force === "visual") cache.video = false;
    const need = dependencies(f, source.selected, cache);
    const q = quote(f, source.selected, cache);
    const amount = (k: string) => q.lines.find((l) => l.key === k)?.amount || 0;
    const steps: JobState["steps"] = [];
    if (need.original) {
      steps.push({
        key: "original",
        label: f.type === "doc" ? "提取文档正文" : "提取语音原文",
        status: "waiting",
        amount: amount("original"),
      });
    }
    if (need.video) {
      steps.push({
        key: "video",
        label: "理解视频画面与内容",
        status: "waiting",
        amount: amount("video"),
      });
    }
    for (const k of ["summary", "structure", "rewrite"] as ResultKey[]) {
      if (source.selected.includes(k)) {
        steps.push({
          key: k,
          label: `生成${optionTitle(k, f.type)}`,
          status: "waiting",
          amount: amount(k),
        });
      }
    }
    return {
      id: Date.now(),
      keys: [...source.selected],
      steps,
      index: 0,
      due: Date.now() + 950,
      estimate: q.total,
      scenario: source.scenario,
      base: cache,
      failed: [],
      paid: false,
    };
  };

  const start = (inDetail = false) => {
    if (!state.file || !state.selected.length || state.job) return;
    if (state.selected.includes("rewrite") && !state.prompt.trim()) {
      showToast("请填写重写要求。");
      return;
    }
    if (quoteResult.total > state.balance) {
      openInfo(
        "积分不足",
        `<p>本次预计需要 <b>${quoteResult.total} 积分</b>，当前可用 <b>${state.balance} 积分</b>。</p><p>请减少生成项后重试。</p>`,
      );
      return;
    }
    const workId = state.workId || `work-${Date.now()}`;
    const next: PageState = {
      ...state,
      workId,
      createdAt: state.createdAt || Date.now(),
      detailWorkId: workId,
      lastKeys: [...state.selected],
      job: prepareJob(state),
      view: inDetail ? "result" : "compose",
      gallery: "works",
      supplement: false,
      templatePreview: null,
    };
    if (state.selected.includes("visual") && !dependencies(effectiveFile!, state.selected, cacheForQuote).video) {
      next.results = { ...next.results, visual: resultData("visual", next as WorkRecord) };
    }
    persist(next);
    if (!inDetail) {
      window.setTimeout(() => worksSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
    }
  };

  const stepOutcome = (source: PageState, step: JobState["steps"][number]) => {
    const job = source.job!;
    const k = step.key;
    const sc = job.scenario;
    if (k === "original" && sc === "nospeech" && source.file?.type !== "doc") {
      return { status: "skipped" as const, reason: "未检测到可识别语音" };
    }
    if (
      (k === "original" && sc === "asr-fail") ||
      (k === "video" && sc === "video-fail") ||
      (k === "rewrite" && sc === "rewrite-fail")
    ) {
      return {
        status: "failed" as const,
        reason: k === "original" ? "原文提取失败" : k === "video" ? "画面分析失败" : "重写生成失败",
      };
    }
    if (["summary", "structure", "rewrite"].includes(k)) {
      const noVoice = sc === "nospeech" && source.file?.type === "video";
      if (!(k === "summary" && noVoice) && !source.cache.original) {
        return { status: "skipped" as const, reason: "缺少有效原文" };
      }
      if (k === "summary" && source.file?.type === "video" && !source.cache.video) {
        return { status: "skipped" as const, reason: "画面分析未完成" };
      }
    }
    return { status: "success" as const };
  };

  useEffect(() => {
    if (!state.job) return undefined;
    const job = state.job;
    const step = job.steps[job.index];
    if (!step) {
      const raw = job.steps.filter((s) => s.status === "success").reduce((sum, s) => sum + s.amount, 0);
      const charged = raw ? Math.ceil(raw - 1e-9) : 0;
      const results = { ...state.results };
      for (const k of job.keys) {
        if (!results[k]) results[k] = { status: "skipped", reason: "必要处理未完成" };
      }
      const active =
        RESULT_ORDER.find((k) => job.keys.includes(k) && results[k]?.status === "success") ||
        RESULT_ORDER.find((k) => results[k]?.status === "success") ||
        job.keys[0];
      persist({
        ...state,
        results,
        charged: state.charged + charged,
        lastCharge: charged,
        lastScenario: job.scenario,
        warnings: job.failed,
        lastKeys: job.keys,
        active,
        job: null,
        force: null,
      });
      return undefined;
    }
    const wait = Math.max(50, job.due - Date.now());
    tickTimer.current = window.setTimeout(() => {
      const nextJob: JobState = {
        ...job,
        steps: job.steps.map((s, i) =>
          i === job.index && s.status === "waiting" ? { ...s, status: "running" } : s,
        ),
      };
      const current = nextJob.steps[nextJob.index];
      if (Date.now() < nextJob.due) {
        persist({ ...state, job: nextJob });
        return;
      }
      const outcome = stepOutcome(state, current);
      const steps = nextJob.steps.map((s, i) => (i === nextJob.index ? { ...s, ...outcome } : s));
      let cache = { ...state.cache };
      let results = { ...state.results };
      const failed = [...nextJob.failed];
      if (outcome.status === "success") {
        if (current.key === "original") {
          cache = { ...cache, original: true };
          results = { ...results, original: resultData("original", { ...state, cache } as WorkRecord) };
        } else if (current.key === "video") {
          cache = { ...cache, video: true };
          if (nextJob.keys.includes("visual")) {
            results = { ...results, visual: resultData("visual", { ...state, cache } as WorkRecord) };
          }
        } else {
          results = {
            ...results,
            [current.key]: resultData(current.key as ResultKey, { ...state, cache } as WorkRecord),
          };
        }
      } else {
        failed.push(`${current.label}：${outcome.reason}`);
        const resultKey = (current.key === "video" ? "visual" : current.key) as ResultKey;
        if (nextJob.keys.includes(resultKey) && results[resultKey]?.status !== "success") {
          results = { ...results, [resultKey]: { status: outcome.status, reason: outcome.reason } };
        }
      }
      persist({
        ...state,
        cache,
        results,
        job: { ...nextJob, steps, failed, index: nextJob.index + 1, due: Date.now() + 950 },
      });
    }, wait);
    return () => window.clearTimeout(tickTimer.current);
  }, [state.job, persist, state]);

  const openWork = (id: string) => {
    persist({ ...state, templatePreview: null, detailWorkId: id, view: "result" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openTemplate = (key: "video" | "audio" | "doc") => {
    persist({ ...state, templatePreview: buildTemplatePreview(key), view: "result" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const useTemplate = () => {
    if (state.job || !state.templatePreview?.templateKey) return;
    uploadSerial.current += 1;
    selectFile({ ...SAMPLE_FILES[state.templatePreview.templateKey] });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const showWorks = () => {
    persist({ ...state, templatePreview: null, view: "compose", gallery: "works" });
    window.setTimeout(() => worksSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const backFromDetail = () => {
    if (!state.templatePreview) {
      showWorks();
      return;
    }
    persist({ ...state, templatePreview: null, view: "compose", gallery: "examples" });
    window.setTimeout(() => worksSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const openSupplement = (key?: ResultKey) => {
    if (state.job || detail.isTemplate) return;
    setSupplementDraft({
      workId: detail.workId,
      keys: key ? [key] : [],
      force: key || null,
      prompt: detail.results.rewrite?.prompt || detail.prompt || "",
    });
    setSupplementError("");
    setSupplementOpen(true);
  };

  const supplementQuote = useMemo(() => {
    if (!supplementDraft) return { total: 0, lines: [] as Array<{ key: string; label: string; amount: number }> };
    const cache = { ...detail.cache };
    if (supplementDraft.force === "original") cache.original = false;
    if (supplementDraft.force === "visual") cache.video = false;
    return quote(
      {
        ...detail.file,
        noSpeech: detail.scenario === "nospeech" && detail.file.type !== "doc",
      },
      supplementDraft.keys,
      cache,
    );
  }, [supplementDraft, detail]);

  const submitSupplement = () => {
    const draft = supplementDraft;
    if (!draft || state.job || draft.workId !== detail.workId || !draft.keys.length) return;
    if (draft.keys.includes("rewrite") && !draft.prompt.trim()) return;
    if (supplementQuote.total > state.balance) {
      setSupplementError("积分不足，请减少生成项后重试。");
      return;
    }
    let next = state;
    if (detail.workId !== state.workId) {
      next = { ...state, ...captureWork(detail), detailWorkId: detail.workId };
    }
    setSupplementOpen(false);
    setSupplementDraft(null);
    const prepared: PageState = {
      ...next,
      selected: [...draft.keys],
      prompt: draft.prompt,
      force: draft.force,
      supplement: true,
      templatePreview: null,
    };
    const q = quote(
      {
        ...prepared.file!,
        noSpeech: prepared.scenario === "nospeech" && prepared.file!.type !== "doc",
      },
      prepared.selected,
      {
        ...prepared.cache,
        ...(prepared.force === "original" ? { original: false } : {}),
        ...(prepared.force === "visual" ? { video: false } : {}),
      },
    );
    if (q.total > prepared.balance) {
      setSupplementError("积分不足，请减少生成项后重试。");
      return;
    }
    const workId = prepared.workId || `work-${Date.now()}`;
    persist({
      ...prepared,
      workId,
      createdAt: prepared.createdAt || Date.now(),
      detailWorkId: workId,
      lastKeys: [...prepared.selected],
      job: prepareJob(prepared),
      view: "result",
      gallery: "works",
      supplement: false,
    });
  };

  const downloadResults = (keys?: ResultKey[]) => {
    const text = exportText(detail, keys);
    const blob = new Blob(["\uFEFF" + text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${detail.file.name.replace(/\.[^.]+$/, "").replace(/[\\/:*?"<>|]/g, "_")}${
      keys ? `-${RESULT_META[keys[0]][0]}` : "-全部结果"
    }.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    setInfoOpen(false);
    showToast("已导出 TXT 文件");
  };

  const removeWork = (id: string) => {
    if (state.workId === id && state.job) return false;
    let next: PageState = { ...state, works: state.works.filter((w) => w.workId !== id) };
    if (state.workId === id) {
      next = {
        ...next,
        workId: null,
        file: null,
        selected: [],
        cache: {},
        results: {},
        prompt: "",
        job: null,
        charged: 0,
        supplement: false,
        force: null,
        warnings: [],
        lastKeys: undefined,
        lastScenario: undefined,
      };
    }
    next.detailWorkId = null;
    next.templatePreview = null;
    next.view = "compose";
    next.gallery = "works";
    persist(next);
    return true;
  };

  const copyActive = async () => {
    const r = detail.results[detail.active];
    if (!r?.plain) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(r.plain);
      else {
        const ta = document.createElement("textarea");
        ta.value = r.plain;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        ta.remove();
        if (!ok) throw new Error();
      }
      showToast("已复制");
    } catch {
      showToast("复制失败，请选中文字后手动复制。");
    }
  };

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".detail-more-wrap")) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey as any);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey as any);
    };
  }, []);

  const choices = availableResults(state.file);
  const resultKeys = RESULT_ORDER.filter(
    (k) => detail.results[k] || detail.job?.keys.includes(k),
  );
  const activeResult = detail.results[detail.active] || { status: "pending" as const };
  const progress = workStatus(detail);
  const rewriteInvalid = state.selected.includes("rewrite") && !state.prompt.trim();

  const onGalleryKey = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
    e.preventDefault();
    const tabs: GalleryTab[] = ["examples", "works"];
    const next =
      e.key === "Home"
        ? 0
        : e.key === "End"
          ? tabs.length - 1
          : (index + (e.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length;
    persist({ ...state, gallery: tabs[next] });
  };

  return (
    <div className="ai-transcription-page">
      <div className="main">
        <div className={`page-shell${state.view === "result" ? " detail-active" : ""}`}>
          <section className="page-heading">
            <div>
              <span className="section-kicker">AI Transcription</span>
              <h1>AI 转录</h1>
              <p>从视频、音频到文档，直接获得你需要的内容。</p>
            </div>
          </section>

          {state.view === "compose" ? (
            <section className="compose-grid">
              <section className="panel composer-panel">
                <div className="upload-panel">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp4,.mov,.mp3,.wav,.m4a,.pdf,.docx,.txt"
                    hidden
                    onChange={(e) => e.target.files && handleFiles(e.target.files)}
                  />
                  {!state.file ? (
                    <button
                      type="button"
                      className={`dropzone${dragging ? " dragging" : ""}`}
                      onClick={() => fileInputRef.current?.click()}
                      onDragEnter={(e) => {
                        e.preventDefault();
                        setDragging(true);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragging(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setDragging(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragging(false);
                        handleFiles(e.dataTransfer.files);
                      }}
                    >
                      <span className="upload-icon">
                        <DataIcon name="cloudUpload" />
                      </span>
                      <strong>点击上传或拖拽文件到此处</strong>
                      <span className="upload-limits">支持视频、音频、文档 · 一次一个文件</span>
                    </button>
                  ) : (
                    <div className="file-card">
                      <span className={`sample-icon ${state.file.type === "video" ? "blue" : state.file.type === "audio" ? "amber" : "green"}`}>
                        <DataIcon
                          name={
                            state.file.type === "audio"
                              ? "headphones"
                              : state.file.type === "video"
                                ? "video"
                                : "file"
                          }
                        />
                      </span>
                      <div className="file-info">
                        <h3 title={state.file.name}>{state.file.name}</h3>
                        <p>{fileMeta(state.file)}</p>
                        <span className="file-status">
                          {state.file.sample
                            ? "示例素材 · 已就绪"
                            : state.file.manual
                              ? "文档信息为演示值"
                              : "本地文件 · 已读取信息"}
                        </span>
                      </div>
                      {!state.supplement ? (
                        <div className="file-buttons">
                          <button
                            type="button"
                            className="icon-button"
                            aria-label="更换文件"
                            title="更换文件"
                            disabled={!!state.job}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <DataIcon name="refresh" />
                          </button>
                          <button
                            type="button"
                            className="icon-button"
                            id="removeFile"
                            aria-label="移除文件"
                            title="移除文件"
                            disabled={!!state.job}
                            onClick={resetFile}
                          >
                            <DataIcon name="trash" />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                  {fileError ? (
                    <div className="message error" role="alert">
                      {fileError}
                    </div>
                  ) : null}
                </div>

                <section className="selection-panel" aria-labelledby="selectionHeading">
                  <div className="section-title">
                    <div className="selection-heading">
                      <h2 id="selectionHeading">
                        {state.force ? "重新生成" : state.supplement ? "补充生成" : "选择所需结果"}
                      </h2>
                      <span className="selection-subtitle">{state.file ? "可多选" : "上传后可多选"}</span>
                    </div>
                    <div className="selection-tools">
                      <button type="button" className="text-button small" id="limitButton" onClick={() => setUsageOpen(true)}>
                        使用说明
                        <DataIcon name="info" />
                      </button>
                    </div>
                  </div>
                  <div className="options">
                    {choices.map((k) => {
                      const complete =
                        state.supplement &&
                        state.results[k]?.status === "success" &&
                        state.force !== k;
                      const disabled = !state.file || complete || !!state.job;
                      const checked = state.selected.includes(k);
                      return (
                        <label
                          key={k}
                          title={complete ? "已生成，可在结果页重新生成" : RESULT_META[k][1]}
                          className={`option${checked ? " selected" : ""}${disabled ? " disabled" : ""}`}
                        >
                          <input
                            type="checkbox"
                            value={k}
                            checked={checked}
                            disabled={disabled}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...state.selected, k]
                                : state.selected.filter((x) => x !== k);
                              persist({ ...state, selected: next });
                            }}
                          />
                          <span>
                            {optionTitle(k, state.file?.type)}
                            {complete ? " · 已生成" : ""}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {state.selected.includes("rewrite") ? (
                    <div className="rewrite-field">
                      <label htmlFor="rewritePrompt">
                        重写要求 <span className="required">*</span>
                      </label>
                      <textarea
                        id="rewritePrompt"
                        rows={3}
                        maxLength={1000}
                        disabled={!!state.job}
                        placeholder="例如：改成自然的短视频口播，约300字，保留核心观点。"
                        value={state.prompt}
                        onChange={(e) => persist({ ...state, prompt: e.target.value })}
                      />
                      <span className="field-note">{state.prompt.length} / 1,000</span>
                    </div>
                  ) : null}
                  <p className={`start-hint${rewriteInvalid ? " invalid" : ""}`}>
                    {rewriteInvalid ? "请填写重写要求" : "无需确认原文，直接获得结果"}
                  </p>
                  <div className="submit-row">
                    <details
                      className={`cost-box${state.file && state.selected.length && quoteResult.total ? "" : " is-hidden"}`}
                    >
                      <summary
                        className="points-badge"
                        aria-label="预计积分，点击查看明细"
                        title="按当前参数预估，实际以扣减为准"
                      >
                        <CoinsEstimateIcon />
                        <span>
                          预计消耗 <span className="cost-point">{quoteResult.total}</span> 积分
                        </span>
                      </summary>
                      <div className="cost-popover">
                        <div className="cost-popover-head">
                          <strong>积分预估</strong>
                          <button
                            type="button"
                            className="text-button"
                            onClick={() => openInfo("积分预估规则", PRICE_RULES_HTML)}
                          >
                            计费规则
                            <DataIcon name="info" />
                          </button>
                        </div>
                        <div className="cost-lines">
                          {quoteResult.lines.map((l) => (
                            <div key={l.key}>
                              <span>{l.label}</span>
                              <span>{effectiveFile ? rateLabel(effectiveFile, l.key) : ""}</span>
                            </div>
                          ))}
                        </div>
                        <p className="cost-footnote">
                          {!state.file
                            ? "选择素材和结果后自动计算"
                            : quoteResult.total
                              ? `合计后向上取整 · 1元 = 10积分${state.supplement ? " · 已有基础内容不重复收费" : ""}`
                              : state.selected.length
                                ? "暂无可执行的收费项"
                                : "请选择至少一项结果"}
                        </p>
                      </div>
                    </details>
                    <div className="submit-actions">
                      <button
                        type="button"
                        className="primary start-button"
                        disabled={startInvalid}
                        onClick={() => start(false)}
                      >
                        <span>
                          {state.job
                            ? "生成中"
                            : state.force
                              ? "重新生成"
                              : state.supplement
                                ? "补充生成"
                                : "开始处理"}
                        </span>
                        <DataIcon name="arrow" />
                      </button>
                    </div>
                  </div>
                  {state.supplement ? (
                    <button
                      type="button"
                      className="text-button cancel-supplement"
                      onClick={() =>
                        persist({ ...state, supplement: false, force: null, view: "result" })
                      }
                    >
                      返回已有结果
                    </button>
                  ) : null}
                </section>
              </section>
            </section>
          ) : null}

          {state.view !== "result" ? (
            <section className="works-section" ref={worksSectionRef} aria-label="素材与作品">
              <header className="works-toolbar">
                <div className="gallery-tabs" role="tablist" aria-label="素材与作品">
                  <button
                    className={`gallery-tab${state.gallery === "examples" ? " active" : ""}`}
                    type="button"
                    role="tab"
                    aria-selected={state.gallery === "examples"}
                    aria-controls="examplesPanel"
                    tabIndex={state.gallery === "examples" ? 0 : -1}
                    onClick={() => persist({ ...state, gallery: "examples" })}
                    onKeyDown={(e) => onGalleryKey(e, 0)}
                  >
                    模板
                  </button>
                  <button
                    className={`gallery-tab${state.gallery === "works" ? " active" : ""}`}
                    type="button"
                    role="tab"
                    aria-selected={state.gallery === "works"}
                    aria-controls="worksPanel"
                    tabIndex={state.gallery === "works" ? 0 : -1}
                    onClick={() => persist({ ...state, gallery: "works" })}
                    onKeyDown={(e) => onGalleryKey(e, 1)}
                  >
                    我的作品 <span>{state.works.length}</span>
                  </button>
                </div>
                <span className="works-note">
                  {state.gallery === "works" ? "生成进度与结果保存在这里" : "点击模板，查看内容与结果"}
                </span>
              </header>
              {state.gallery === "examples" ? (
                <div className="examples" id="examplesPanel" role="tabpanel">
                  <div className="template-grid">
                    <button
                      type="button"
                      className="template-card"
                      disabled={!!state.job}
                      aria-label="查看桌面收纳方法模板"
                      onClick={() => openTemplate("video")}
                    >
                      <span className="template-cover desk-cover">
                        <span className="template-type">
                          <DataIcon name="video" />
                          视频转录
                        </span>
                        <svg className="cover-art" viewBox="0 0 480 270" aria-hidden="true">
                          <defs>
                            <linearGradient id="deskWall" x2="1" y2="1">
                              <stop stopColor="#e9e7de" />
                              <stop offset="1" stopColor="#c8d1c8" />
                            </linearGradient>
                          </defs>
                          <path fill="url(#deskWall)" d="M0 0h480v270H0z" />
                          <path fill="#f6f4eb" opacity=".7" d="M290 0h140L270 202H130z" />
                          <path fill="#b9afa0" d="M0 212h480v58H0z" />
                          <path fill="#d5caba" d="M0 206h480v9H0z" />
                          <rect x="136" y="79" width="207" height="117" rx="8" fill="#384849" />
                          <rect x="143" y="86" width="193" height="102" rx="3" fill="#aabcb4" />
                          <path fill="#dbe3d7" d="M143 159l56-52 55 59 42-27 40 49H143z" />
                          <circle cx="296" cy="108" r="12" fill="#ece9d4" />
                          <path d="M226 196v11m-26 3h66" stroke="#52605b" strokeWidth="6" />
                          <path d="M63 207l20-84 38-30" fill="none" stroke="#68736d" strokeWidth="6" />
                          <path d="M106 81q27-7 32 18l-37 10z" fill="#56635b" />
                          <ellipse cx="67" cy="210" rx="24" ry="4" fill="#6e7971" />
                          <path fill="#e9e3d7" d="M384 179h33l-4 31h-25z" />
                          <path
                            d="M400 181v-40m0 22q-25-5-18-22 19 3 18 22m0-8q23-5 18-22-18 6-18 22"
                            fill="#718674"
                            stroke="#718674"
                            strokeWidth="3"
                          />
                          <rect x="92" y="209" width="58" height="6" rx="2" fill="#faf7ee" />
                          <rect x="167" y="218" width="145" height="8" rx="4" fill="#a89e90" />
                        </svg>
                        <span className="template-duration">03:00</span>
                      </span>
                      <span className="template-info">
                        <strong>桌面收纳方法</strong>
                        <span className="template-description">从视频中提炼整理步骤与画面要点</span>
                        <span className="template-bottom">
                          <span>生活技巧 · 视频</span>
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="template-card"
                      disabled={!!state.job}
                      aria-label="查看聊聊专注力模板"
                      onClick={() => openTemplate("audio")}
                    >
                      <span className="template-cover audio-cover">
                        <span className="template-type">
                          <DataIcon name="headphones" />
                          音频转录
                        </span>
                        <span className="audio-cover-title" aria-hidden="true">
                          <small>专注，从减少切换开始</small>
                          <b>聊聊专注力</b>
                        </span>
                        <svg className="audio-wave" viewBox="0 0 400 70" aria-hidden="true">
                          <path
                            d="M8 31v8m12-15v22m12-29v36m12-21v6m12-27v48m12-41v34m12-46v58m12-41v24m12-33v42m12-50v58m12-41v24m12-17v10m12-24v38m12-49v60m12-40v20m12-29v38m12-47v56m12-35v14m12-22v30m12-43v56m12-48v40m12-55v70m12-52v34m12-42v50m12-40v30m12-44v58m12-44v30m12-22v14m12-19v24m12-31v38m12-27v16m12-12v8"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="4"
                            strokeLinecap="round"
                          />
                        </svg>
                        <span className="template-duration">03:00</span>
                      </span>
                      <span className="template-info">
                        <strong>聊聊专注力</strong>
                        <span className="template-description">整理对话观点，提取可执行的建议</span>
                        <span className="template-bottom">
                          <span>知识分享 · 音频</span>
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="template-card"
                      disabled={!!state.job}
                      aria-label="查看用户访谈纪要模板"
                      onClick={() => openTemplate("doc")}
                    >
                      <span className="template-cover document-cover">
                        <span className="template-type">
                          <DataIcon name="file" />
                          文档提取
                        </span>
                        <span className="document-sheet" aria-hidden="true">
                          <span className="document-eyebrow">RESEARCH NOTES</span>
                          <b>用户访谈纪要</b>
                          <span className="document-divider" />
                          <span className="document-line">访谈目标与主要发现</span>
                          <span className="document-line muted-line">内容整理 · 使用习惯 · 产品期待</span>
                          <span className="document-rules" />
                        </span>
                        <span className="template-duration">10,000 字符</span>
                      </span>
                      <span className="template-info">
                        <strong>用户访谈纪要</strong>
                        <span className="template-description">提取文档正文，归纳发现与后续事项</span>
                        <span className="template-bottom">
                          <span>用户研究 · 文档</span>
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
              ) : (
                <div id="worksPanel" role="tabpanel">
                  <div className="works-grid">
                    {state.works.map((w) => {
                      const status = workStatus(w);
                      const date = new Date(w.createdAt).toLocaleString("zh-CN", {
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                      const tags = Array.from(
                        new Set([
                          ...RESULT_ORDER.filter((k) => w.results[k]?.status === "success"),
                          ...(w.job?.keys || w.lastKeys || w.selected || []),
                        ]),
                      );
                      return (
                        <article className="work-card" key={w.workId}>
                          <div className="work-card-head">
                            <span className="work-icon">
                              <DataIcon
                                name={
                                  w.file.type === "video"
                                    ? "video"
                                    : w.file.type === "audio"
                                      ? "headphones"
                                      : "file"
                                }
                              />
                            </span>
                            <span className={`work-state ${status.kind}`}>{status.label}</span>
                          </div>
                          <h3 title={w.file.name}>{w.file.name}</h3>
                          <p className="work-meta">{fileMeta(w.file)}</p>
                          <div className="work-tags">
                            {tags.map((k) => (
                              <span key={k}>{RESULT_META[k][0]}</span>
                            ))}
                          </div>
                          {w.job ? (
                            <div
                              className="work-progress"
                              role="progressbar"
                              aria-label="生成进度"
                              aria-valuemin={0}
                              aria-valuemax={100}
                              aria-valuenow={status.progress}
                            >
                              <span style={{ width: `${status.progress}%` }} />
                            </div>
                          ) : null}
                          <p className="work-message">
                            {status.message}
                            {w.job ? ` · ${status.progress}%` : ""}
                          </p>
                          <div className="work-card-foot">
                            <span>{date}</span>
                            <button
                              type="button"
                              className="text-button"
                              disabled={!!w.job && !status.success}
                              onClick={() => openWork(w.workId)}
                            >
                              {w.job ? "查看已完成内容" : "查看结果"}
                              <DataIcon name="chevron" />
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                  {!state.works.length ? (
                    <div className="works-empty">
                      <DataIcon name="folder" />
                      <h3>还没有转录作品</h3>
                      <p>上传素材并开始处理，进度和结果会保存在这里。</p>
                    </div>
                  ) : null}
                </div>
              )}
            </section>
          ) : (
            <section>
              <div className="detail-topbar">
                <button
                  type="button"
                  className="detail-back"
                  aria-label={detail.isTemplate ? "返回模板" : "返回我的作品"}
                  title={detail.isTemplate ? "返回模板" : "返回我的作品"}
                  onClick={backFromDetail}
                >
                  <DataIcon name="chevron" />
                </button>
                <div className="detail-tools">
                  {detail.isTemplate ? (
                    <button
                      type="button"
                      className="secondary"
                      disabled={!!state.job}
                      onClick={useTemplate}
                    >
                      <DataIcon name="plus" />
                      使用模板
                    </button>
                  ) : null}
                  {!detail.isTemplate ? (
                    <button
                      type="button"
                      className="toolbar-export"
                      disabled={!progress.success}
                      onClick={() =>
                        openInfo(
                          "导出结果",
                          "",
                        )
                      }
                    >
                      <DataIcon name="download" />
                      导出
                    </button>
                  ) : null}
                  {!detail.isTemplate ? (
                    <div className="detail-more-wrap">
                      <button
                        type="button"
                        className="detail-more-button"
                        aria-label="更多作品操作"
                        aria-expanded={moreOpen}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMoreOpen((v) => !v);
                        }}
                      >
                        <DataIcon name="more" />
                      </button>
                      {moreOpen ? (
                        <div className="detail-more-menu">
                          <button
                            type="button"
                            className="detail-delete-button"
                            disabled={!!detail.job}
                            onClick={() => {
                              if (detail.job) return;
                              const id = detail.workId;
                              setMoreOpen(false);
                              openInfo("删除作品", id);
                            }}
                          >
                            <DataIcon name="trash" />
                            删除作品
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="result-summary">
                <span className="result-file-icon">
                  <DataIcon
                    name={
                      detail.file?.type === "video"
                        ? "video"
                        : detail.file?.type === "audio"
                          ? "headphones"
                          : "file"
                    }
                  />
                </span>
                <h1>{detail.file?.name}</h1>
                <p>
                  {detail.file ? fileMeta(detail.file) : ""}
                  {detail.isTemplate ? " · 模板内容预览" : ` · 累计消耗 ${detail.charged} 积分`}
                </p>
                <span className="status-badge">
                  {detail.isTemplate ? "模板预览" : progress.label}
                </span>
              </div>
              {(() => {
                const warnings: string[] = [];
                if (detail.file && !detail.file.sample) {
                  warnings.push(
                    "当前为交互演示：除TXT原文外，生成内容为内置示例，并非对上传文件的真实分析。",
                  );
                }
                if ((detail.job?.scenario || detail.lastScenario) === "nospeech" && detail.file?.type === "video") {
                  warnings.push("未检测到可识别语音，视频总结仅依据画面。");
                }
                if (detail.warnings?.length) warnings.push(...detail.warnings);
                return warnings.length ? (
                  <div id="resultWarning" className="message warning">
                    {warnings.join("；")}
                  </div>
                ) : null;
              })()}
              {detail.job ? (
                <div className="detail-progress" role="status" aria-live="polite">
                  <DataIcon name="sparkles" />
                  <div>
                    <strong>{progress.message}</strong>
                    <p>已有内容可继续阅读，完成后自动更新当前作品。</p>
                  </div>
                  <span>{progress.progress}%</span>
                </div>
              ) : null}
              <div className="result-layout">
                <div className="result-tabs-row">
                  <nav className="result-tabs" aria-label="生成结果">
                    {resultKeys.map((k) => (
                      <button
                        key={k}
                        type="button"
                        className={`result-tab${detail.active === k ? " active" : ""}`}
                        aria-pressed={detail.active === k}
                        onClick={() => {
                          if (state.templatePreview) {
                            persist({
                              ...state,
                              templatePreview: { ...state.templatePreview, active: k },
                            });
                          } else if (state.detailWorkId === state.workId) {
                            persist({ ...state, active: k });
                          } else {
                            persist({
                              ...state,
                              works: state.works.map((w) =>
                                w.workId === state.detailWorkId ? { ...w, active: k } : w,
                              ),
                            });
                          }
                        }}
                      >
                        <DataIcon name={RESULT_META[k][2]} />
                        <span>{k === "original" ? "原始内容" : optionTitle(k, detail.file?.type)}</span>
                        {detail.job?.keys.includes(k) ? (
                          <small>生成中</small>
                        ) : detail.results[k]?.status !== "success" ? (
                          <small>未完成</small>
                        ) : null}
                      </button>
                    ))}
                  </nav>
                  {!detail.isTemplate ? (
                    <button
                      type="button"
                      className="text-button supplement-inline"
                      disabled={!!state.job}
                      onClick={() => openSupplement()}
                    >
                      <DataIcon name="plus" />
                      补充生成
                    </button>
                  ) : null}
                </div>
                <article className="panel result-paper">
                  <div className="paper-toolbar">
                    <div>
                      <h2>
                        {detail.active === "original"
                          ? "原始内容"
                          : optionTitle(detail.active, detail.file?.type)}
                      </h2>
                      <div className="paper-badges">
                        <span>{activeResult.basis || "暂未生成"}</span>
                        <span>演示结果</span>
                      </div>
                    </div>
                    <div className="paper-actions">
                      {!detail.isTemplate &&
                      activeResult.status !== "pending" &&
                      !(activeResult.status === "success" && detail.active !== "rewrite") ? (
                        <button
                          type="button"
                          className="text-button"
                          disabled={!!state.job}
                          onClick={() => openSupplement(detail.active)}
                        >
                          <DataIcon name="refresh" />
                          <span>{activeResult.status === "success" ? "调整重写要求" : "重试"}</span>
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="secondary"
                        disabled={activeResult.status !== "success"}
                        onClick={copyActive}
                      >
                        <DataIcon name="copy" />
                        复制
                      </button>
                    </div>
                  </div>
                  <div className="result-content">
                    {detail.active === "rewrite" && activeResult.prompt ? (
                      <p className="rewrite-result-note">
                        本次要求：{activeResult.prompt}（原型仅演示要求保存与重新生成，正文为示例）
                      </p>
                    ) : null}
                    {activeResult.status === "success" ? (
                      <div dangerouslySetInnerHTML={{ __html: activeResult.html || "" }} />
                    ) : activeResult.status === "pending" ? (
                      <div className="pending-result">
                        <DataIcon name="sparkles" />
                        <h3>正在生成{optionTitle(detail.active, detail.file?.type)}</h3>
                        <p>可以先查看其他结果，完成后会自动显示。</p>
                      </div>
                    ) : (
                      <div className="failure-empty">
                        <DataIcon name="info" />
                        <h3>暂未获得结果</h3>
                        <p>{activeResult.reason || "请重新尝试"}</p>
                      </div>
                    )}
                  </div>
                  <div className="paper-footer">
                    AI生成内容仅供参考，请核对关键事实。
                    <span>{activeResult.plain ? `${[...activeResult.plain].length}字符` : ""}</span>
                  </div>
                </article>
              </div>
            </section>
          )}
        </div>
      </div>

      <dialog
        ref={infoDialogRef}
        onClose={() => setInfoOpen(false)}
        onClick={(e) => {
          if (e.target === e.currentTarget) setInfoOpen(false);
        }}
      >
          <div className="dialog-head">
            <h2>{infoTitle}</h2>
            <button type="button" className="icon-button" aria-label="关闭" onClick={() => setInfoOpen(false)}>
              <DataIcon name="x" />
            </button>
          </div>
          {infoTitle === "导出结果" ? (
            <div>
              <p>导出为 TXT 文件，可保存或继续编辑。</p>
              <div className="export-choices">
                <button
                  type="button"
                  className="secondary"
                  disabled={detail.results[detail.active]?.status !== "success"}
                  onClick={() => downloadResults([detail.active])}
                >
                  导出当前结果
                </button>
                <button type="button" className="primary" onClick={() => downloadResults()}>
                  导出全部已完成结果
                </button>
              </div>
            </div>
          ) : infoTitle === "删除作品" ? (
            <div>
              <p>
                确定删除「{detail.file?.name}」及其全部生成结果？删除后无法恢复。
              </p>
              <div className="delete-actions">
                <button type="button" className="secondary" onClick={() => setInfoOpen(false)}>
                  取消
                </button>
                <button
                  type="button"
                  className="danger-button"
                  onClick={() => {
                    const id = detail.workId;
                    setInfoOpen(false);
                    if (removeWork(id)) showToast("作品已删除");
                  }}
                >
                  删除作品
                </button>
              </div>
            </div>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: infoHtml }} />
          )}
      </dialog>

      <dialog
          ref={usageDialogRef}
          className="usage-dialog"
          aria-labelledby="usageTitle"
          onClose={() => setUsageOpen(false)}
          onClick={(e) => {
            if (e.target === e.currentTarget) setUsageOpen(false);
          }}
        >
          <header className="usage-header">
            <div>
              <h2 id="usageTitle">
                <DataIcon name="list" />
                AI 转录使用说明
              </h2>
              <p>一次上传一个文件，大小与时长、页数或字符数需同时符合要求。</p>
            </div>
            <button type="button" className="usage-close" aria-label="关闭使用说明" onClick={() => setUsageOpen(false)}>
              <DataIcon name="x" />
            </button>
          </header>
          <div className="usage-body">
            <div className="usage-requirements">
              <section className="usage-item">
                <span className="usage-item-icon video-tone">
                  <DataIcon name="video" />
                </span>
                <div>
                  <h3>视频</h3>
                  <p>
                    支持 <strong>MP4、MOV</strong> 格式
                  </p>
                  <p>
                    大小 ≤ <strong>500MB</strong> · 时长 ≤ <strong>30分钟</strong>
                  </p>
                </div>
              </section>
              <section className="usage-item">
                <span className="usage-item-icon audio-tone">
                  <DataIcon name="headphones" />
                </span>
                <div>
                  <h3>音频</h3>
                  <p>
                    支持 <strong>MP3、WAV、M4A</strong> 格式
                  </p>
                  <p>
                    大小 ≤ <strong>200MB</strong> · 时长 ≤ <strong>120分钟</strong>
                  </p>
                </div>
              </section>
              <section className="usage-item">
                <span className="usage-item-icon document-tone">
                  <DataIcon name="file" />
                </span>
                <div>
                  <h3>文件</h3>
                  <p>
                    <strong>PDF（文字型）</strong>：≤ <strong>20MB</strong> · ≤ <strong>100页</strong> · ≤{" "}
                    <strong>30,000字符</strong>
                  </p>
                  <p>
                    <strong>DOCX</strong>：≤ <strong>20MB</strong> · ≤ <strong>30,000字符</strong>
                  </p>
                  <p>
                    <strong>TXT（UTF-8）</strong>：≤ <strong>1MB</strong> · ≤ <strong>30,000字符</strong>
                  </p>
                </div>
              </section>
            </div>
            <aside className="usage-tips">
              <DataIcon name="info" />
              <div>
                <h3>温馨提示</h3>
                <ul>
                  <li>音视频尽量保持人声清晰，避免明显背景噪音。</li>
                  <li>无声视频可选择画面概述或视频总结。</li>
                  <li>暂不支持扫描件、加密文档及复杂表格解析。</li>
                  <li>处理进度与结果可在「我的作品」中查看。</li>
                </ul>
              </div>
            </aside>
          </div>
        </dialog>

      <dialog
          ref={supplementDialogRef}
          aria-labelledby="supplementTitle"
          onClose={() => {
            setSupplementOpen(false);
            setSupplementDraft(null);
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSupplementOpen(false);
              setSupplementDraft(null);
            }
          }}
        >
          <div className="dialog-head">
            <h2 id="supplementTitle">
              {supplementDraft?.force
                ? supplementDraft.force === "rewrite" && detail.results[supplementDraft.force]?.status === "success"
                  ? "调整重写要求"
                  : `重试${RESULT_META[supplementDraft.force][0]}`
                : "补充生成"}
            </h2>
            <button
              type="button"
              className="icon-button"
              aria-label="关闭"
              onClick={() => {
                setSupplementOpen(false);
                setSupplementDraft(null);
              }}
            >
              <DataIcon name="x" />
            </button>
          </div>
          <p className="supplement-source">{detail.file?.name}</p>
          <p className="supplement-note">
            {supplementDraft?.force
              ? "生成完成后更新当前结果，失败时保留已有内容。"
              : availableResults(detail.file).filter((k) => detail.results[k]?.status !== "success").length
                ? "只需选择缺少的结果，已有内容会自动复用。"
                : "所有结果均已生成。如需修改重写内容，可关闭弹窗后在重写结果中点击“调整重写要求”。"}
          </p>
          <div className="options">
            {(supplementDraft?.force
              ? [supplementDraft.force]
              : availableResults(detail.file).filter((k) => detail.results[k]?.status !== "success")
            ).map((k) => (
              <label
                key={k}
                className={`option${supplementDraft?.keys.includes(k) ? " selected" : ""}`}
              >
                <input
                  type="checkbox"
                  value={k}
                  checked={!!supplementDraft?.keys.includes(k)}
                  onChange={(e) => {
                    if (!supplementDraft) return;
                    setSupplementError("");
                    setSupplementDraft({
                      ...supplementDraft,
                      keys: e.target.checked
                        ? [...supplementDraft.keys, k]
                        : supplementDraft.keys.filter((x) => x !== k),
                    });
                  }}
                />
                <span>{optionTitle(k, detail.file?.type)}</span>
              </label>
            ))}
          </div>
          {supplementDraft?.keys.includes("rewrite") ? (
            <div className="rewrite-field">
              <label htmlFor="supplementPrompt">
                重写要求 <span className="required">*</span>
              </label>
              <textarea
                id="supplementPrompt"
                rows={3}
                maxLength={1000}
                placeholder="例如：改成自然的短视频口播，约300字。"
                value={supplementDraft.prompt}
                onChange={(e) => setSupplementDraft({ ...supplementDraft, prompt: e.target.value })}
              />
            </div>
          ) : null}
          {supplementError ? (
            <p className="message error" role="alert">
              {supplementError}
            </p>
          ) : null}
          <div className="supplement-footer">
            <div className="supplement-cost">
              <span
                className={`supplement-points${supplementDraft?.keys.length && supplementQuote.total ? "" : " is-hidden"}`}
              >
                <CoinsEstimateIcon />
                <span>
                  预计消耗 <span className="cost-point">{supplementQuote.total}</span> 积分
                </span>
              </span>
              <p>合计取整 · 已有基础内容不重复收费</p>
            </div>
            <button
              type="button"
              className="primary"
              disabled={
                !supplementDraft?.keys.length ||
                (supplementDraft.keys.includes("rewrite") && !supplementDraft.prompt.trim())
              }
              onClick={submitSupplement}
            >
              开始生成
            </button>
          </div>
        </dialog>

      <dialog
        ref={docDialogRef}
        onClose={() => setDocDemo(null)}
        onClick={(e) => e.target === e.currentTarget && setDocDemo(null)}
      >
          <div className="dialog-head">
            <h2>文档上传演示</h2>
            <button type="button" className="icon-button" aria-label="关闭" onClick={() => setDocDemo(null)}>
              <DataIcon name="x" />
            </button>
          </div>
          <div>
            <p>当前原型不解析 PDF / Word 正文。可以填写模拟页数与字符数，体验校验及积分预估；生成结果使用示例内容。</p>
            {docDemo?.ext === "pdf" ? (
              <>
                <label htmlFor="demoPages">模拟页数</label>
                <input
                  id="demoPages"
                  type="number"
                  min={1}
                  max={1000}
                  value={docDemo.pages}
                  onChange={(e) => setDocDemo({ ...docDemo, pages: e.target.value })}
                />
              </>
            ) : null}
            <label htmlFor="demoChars">模拟正文字符数</label>
            <input
              id="demoChars"
              type="number"
              min={1}
              max={100000}
              value={docDemo?.chars || ""}
              onChange={(e) => docDemo && setDocDemo({ ...docDemo, chars: e.target.value })}
            />
            <button className="primary dialog-save" type="button" onClick={confirmDocDemo}>
              使用模拟信息
            </button>
          </div>
        </dialog>

      {toast ? (
        <div role="status" className="toast">
          {toast}
        </div>
      ) : null}
    </div>
  );
};

export default AiTranscription;
